/** Server accepts up to 2MB; we aim lower so uploads stay snappy. */
export const AVATAR_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const AVATAR_TARGET_BYTES = 512 * 1024;
export const AVATAR_OUTPUT_SIZE = 512;
export const AVATAR_CROP_VIEW_SIZE = 280;
export const AVATAR_MIN_ZOOM = 1;
export const AVATAR_MAX_ZOOM = 3;

/** Cover / banner: full-width strip; crop aspect follows the live banner box */
export const BANNER_ASPECT = 3; // fallback when size can't be measured
export const BANNER_DISPLAY_HEIGHT_MOBILE = 144; // Tailwind h-36
export const BANNER_DISPLAY_HEIGHT_DESKTOP = 192; // Tailwind h-48
export const BANNER_DISPLAY_MQ = "(min-width: 640px)";
export const BANNER_CROP_VIEW_MAX_WIDTH = 520;
export const BANNER_OUTPUT_WIDTH = 1500;
export const BANNER_TARGET_BYTES = 1024 * 1024;

/** @deprecated Prefer getBannerCropViewSize(); kept for callers that need a static default */
export const BANNER_CROP_VIEW_WIDTH = 420;
export const BANNER_CROP_VIEW_HEIGHT = Math.round(
  BANNER_CROP_VIEW_WIDTH / BANNER_ASPECT
);
export const BANNER_OUTPUT_HEIGHT = Math.round(
  BANNER_OUTPUT_WIDTH / BANNER_ASPECT
);

/**
 * Live profile banner box (full-width × fixed height). Cropping to this aspect
 * makes object-cover a no-op on the same viewport — WYSIWYG.
 */
export function getBannerDisplayMetrics() {
  if (typeof window === "undefined") {
    return {
      width: 672,
      height: BANNER_DISPLAY_HEIGHT_DESKTOP,
      aspect: 672 / BANNER_DISPLAY_HEIGHT_DESKTOP,
    };
  }
  const desktop = window.matchMedia(BANNER_DISPLAY_MQ).matches;
  const height = desktop
    ? BANNER_DISPLAY_HEIGHT_DESKTOP
    : BANNER_DISPLAY_HEIGHT_MOBILE;
  const bannerEl = document.querySelector("[data-social-banner]");
  const mainEl = document.querySelector("main");
  const width = Math.max(
    1,
    bannerEl?.clientWidth || mainEl?.clientWidth || window.innerWidth
  );
  return { width, height, aspect: width / height };
}

/**
 * Scale the live banner aspect down to fit the crop dialog.
 */
export function getBannerCropViewSize(
  aspect = getBannerDisplayMetrics().aspect,
  maxWidth = BANNER_CROP_VIEW_MAX_WIDTH
) {
  const safeAspect = aspect > 0 ? aspect : BANNER_ASPECT;
  let viewW = maxWidth;
  let viewH = Math.round(viewW / safeAspect);
  const maxH = 220;
  if (viewH > maxH) {
    viewH = maxH;
    viewW = Math.round(viewH * safeAspect);
  }
  return { viewW, viewH, aspect: safeAspect };
}

/**
 * Output pixel size for a given banner aspect (long edge capped).
 */
export function getBannerOutputSize(aspect = BANNER_ASPECT) {
  const safeAspect = aspect > 0 ? aspect : BANNER_ASPECT;
  const outputW = BANNER_OUTPUT_WIDTH;
  const outputH = Math.max(1, Math.round(outputW / safeAspect));
  return { outputW, outputH };
}

/**
 * Scale that makes the image cover a square viewport at zoom = 1.
 */
export function coverScale(imageWidth, imageHeight, viewSize) {
  return viewSize / Math.min(imageWidth, imageHeight);
}

/**
 * Scale that makes the image cover a rectangular viewport at zoom = 1.
 */
export function coverScaleRect(imageWidth, imageHeight, viewW, viewH) {
  return Math.max(viewW / imageWidth, viewH / imageHeight);
}

/**
 * Clamp pan so the circular viewport stays fully covered by the image.
 */
export function clampAvatarOffset(
  imageWidth,
  imageHeight,
  viewSize,
  zoom,
  offsetX,
  offsetY
) {
  const scale = coverScale(imageWidth, imageHeight, viewSize) * zoom;
  const drawnW = imageWidth * scale;
  const drawnH = imageHeight * scale;
  const maxX = Math.max(0, (drawnW - viewSize) / 2);
  const maxY = Math.max(0, (drawnH - viewSize) / 2);
  return {
    offsetX: Math.min(maxX, Math.max(-maxX, offsetX)),
    offsetY: Math.min(maxY, Math.max(-maxY, offsetY)),
  };
}

/**
 * Clamp pan so a rectangular viewport stays fully covered by the image.
 */
export function clampRectOffset(
  imageWidth,
  imageHeight,
  viewW,
  viewH,
  zoom,
  offsetX,
  offsetY
) {
  const scale = coverScaleRect(imageWidth, imageHeight, viewW, viewH) * zoom;
  const drawnW = imageWidth * scale;
  const drawnH = imageHeight * scale;
  const maxX = Math.max(0, (drawnW - viewW) / 2);
  const maxY = Math.max(0, (drawnH - viewH) / 2);
  return {
    offsetX: Math.min(maxX, Math.max(-maxX, offsetX)),
    offsetY: Math.min(maxY, Math.max(-maxY, offsetY)),
  };
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ image: img, objectUrl: url });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Could not encode image"));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * Encode canvas as JPEG/WebP under maxBytes by lowering quality, then size.
 */
export async function compressCanvasToBlob(
  canvas,
  {
    maxBytes = AVATAR_TARGET_BYTES,
    hardMaxBytes = AVATAR_MAX_UPLOAD_BYTES,
    preferredType = "image/jpeg",
  } = {}
) {
  const types =
    preferredType === "image/webp"
      ? ["image/webp", "image/jpeg"]
      : ["image/jpeg", "image/webp"];

  let working = canvas;
  for (let sizeAttempt = 0; sizeAttempt < 4; sizeAttempt += 1) {
    for (const type of types) {
      for (let q = 0.92; q >= 0.45; q -= 0.08) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await canvasToBlob(working, type, q);
        if (blob.size <= maxBytes) return blob;
      }
      // eslint-disable-next-line no-await-in-loop
      const low = await canvasToBlob(working, type, 0.4);
      if (low.size <= hardMaxBytes) {
        if (low.size <= maxBytes) return low;
      }
    }

    const next = document.createElement("canvas");
    const ratio = working.height / working.width;
    const nextW = Math.max(128, Math.round(working.width * 0.75));
    const nextH = Math.max(64, Math.round(nextW * ratio));
    next.width = nextW;
    next.height = nextH;
    const ctx = next.getContext("2d");
    ctx.drawImage(working, 0, 0, nextW, nextH);
    working = next;
  }

  const fallback = await canvasToBlob(working, "image/jpeg", 0.35);
  if (fallback.size > hardMaxBytes) {
    throw new Error(
      `Image is still too large after compression (${Math.round(
        fallback.size / 1024
      )}KB). Try a smaller photo.`
    );
  }
  return fallback;
}

/**
 * Rasterize the circular viewport to a square avatar blob.
 */
export async function cropAvatarToFile({
  image,
  viewSize = AVATAR_CROP_VIEW_SIZE,
  zoom,
  offsetX,
  offsetY,
  outputSize = AVATAR_OUTPUT_SIZE,
  maxBytes = AVATAR_TARGET_BYTES,
  fileName = "avatar.jpg",
}) {
  const { offsetX: x, offsetY: y } = clampAvatarOffset(
    image.naturalWidth,
    image.naturalHeight,
    viewSize,
    zoom,
    offsetX,
    offsetY
  );
  const scale = coverScale(image.naturalWidth, image.naturalHeight, viewSize) * zoom;
  const imgLeft = viewSize / 2 + x - (image.naturalWidth * scale) / 2;
  const imgTop = viewSize / 2 + y - (image.naturalHeight * scale) / 2;
  const sx = (0 - imgLeft) / scale;
  const sy = (0 - imgTop) / scale;
  const sSize = viewSize / scale;

  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputSize, outputSize);
  ctx.drawImage(image, sx, sy, sSize, sSize, 0, 0, outputSize, outputSize);

  const blob = await compressCanvasToBlob(canvas, { maxBytes });
  const ext = blob.type === "image/webp" ? "webp" : "jpg";
  const base = String(fileName || "avatar").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.${ext}`, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

/**
 * Rasterize a rectangular cover viewport to a banner blob.
 */
export async function cropBannerToFile({
  image,
  viewW = BANNER_CROP_VIEW_WIDTH,
  viewH = BANNER_CROP_VIEW_HEIGHT,
  zoom,
  offsetX,
  offsetY,
  outputW = BANNER_OUTPUT_WIDTH,
  outputH = BANNER_OUTPUT_HEIGHT,
  maxBytes = BANNER_TARGET_BYTES,
  fileName = "banner.jpg",
}) {
  const { offsetX: x, offsetY: y } = clampRectOffset(
    image.naturalWidth,
    image.naturalHeight,
    viewW,
    viewH,
    zoom,
    offsetX,
    offsetY
  );
  const scale =
    coverScaleRect(image.naturalWidth, image.naturalHeight, viewW, viewH) *
    zoom;
  const imgLeft = viewW / 2 + x - (image.naturalWidth * scale) / 2;
  const imgTop = viewH / 2 + y - (image.naturalHeight * scale) / 2;
  const sx = (0 - imgLeft) / scale;
  const sy = (0 - imgTop) / scale;
  const sW = viewW / scale;
  const sH = viewH / scale;

  const canvas = document.createElement("canvas");
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputW, outputH);
  ctx.drawImage(image, sx, sy, sW, sH, 0, 0, outputW, outputH);

  const blob = await compressCanvasToBlob(canvas, {
    maxBytes,
    hardMaxBytes: AVATAR_MAX_UPLOAD_BYTES,
  });
  const ext = blob.type === "image/webp" ? "webp" : "jpg";
  const base = String(fileName || "banner").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.${ext}`, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
